import AddIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { default as DeleteIcon } from "@mui/icons-material/HighlightOffRounded";
import { isEmpty, isObject } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetProgramTemplateById } from "../../../../../api/services/programTemplate/programTemplateApis";
import { InformationBadge } from "../../../../../common/informationBadge/informationBadge";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  MultiOptionsDataType,
  OptionsDataType,
  OptionsDataTypeWithNode,
  WarningConfirmationModal
} from "../../../../../components";
import { ButtonWithIcon } from "../../../../../components/buttonWithIcon/ButtonWithIcon";
import {
  EMPTY_VALUE_DASH,
  gridSize3Column,
  MAX_LENGTH_100,
  PAGE_IDS,
} from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import {
  getCountryIdsAsStringArray,
  getFormArrayInstanceHasErrors,
  hasCountryNameKey,
  hasKey,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOptionForCountry,
} from "../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import { Separator } from "../../../../../styles/common.style";
import {
  ProgramTemplate,
  ProgramTemplateFormKey,
} from "../../../modelsAndSchemas/Models";
import {
  billingAndPurchaseOrderKeys,
  clientSpecificContentKeys,
} from "../../../siteCore/SiteCoreKeys";

export const emptyProgramTemplateItem: ProgramTemplate = {
  programTemplateName: "",
  programTemplateId: "",
  solutionOfferingsName: EMPTY_VALUE_DASH,
  programClassName: EMPTY_VALUE_DASH,
  countries: [],
};

interface CreateProgramTemplatesProps {
  programTemplates: OptionsDataType[] | OptionsDataTypeWithNode[];
  countries: MultiOptionsDataType[];
  isEdit?: boolean;
}

const pageId = PAGE_IDS.CLIENT_SPECIFIC_CONTENT_PROGRAM_TEMPLATE;
export const CreateProgramTemplates: FC<CreateProgramTemplatesProps> = ({
  programTemplates: programTemplatesData,
  isEdit = false,
  countries: allCountriesList,
}) => {
  const { commonLabels, fetchLabel, fetchValidationMessage } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { displayToast } = useToast();
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const dispatch = useDispatch();
  const [programTemplateAccordionStatus, setProgramTemplateAccordionStatus] =
    useState<boolean[]>([]);
  const [preSelectedCountries, setPreSelectedCountries] = useState<string[][]>([
    [],
  ]);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const indexRef = useRef<number>();
  const programTemplateSelectedIndexRef = useRef<number>();
  const [countriesList, setCountriesList] = useState<MultiOptionsDataType[][]>([
    [],
  ]);

  const {
    mutateAsync: getProgramTemplateById,
    data: programTemplateDataById,
    isPending,
    isError,
  } = useGetProgramTemplateById();

  const {
    inputPlaceHolder,
    selectPlaceHolder,
    selectedCountries,
    loadingPleaseWait,
  } = commonLabels;

  const {
    clientSpecificContentTitle,
    programTemplate,
    programClass,
    solutionOfferings,
    country,
    programTemplateHeading,
    addProgramTemplateNote,
    editProgramTemplateNote,
    addTemplateButtonText,
  } = clientSpecificContentKeys;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
        message: loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    if (isError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isError]);

  const {
    fields: programTemplatesArray,
    append,
    remove,
  } = useFieldArray({
    control,
    name: ProgramTemplateFormKey.programTemplates,
    keyName: "ptUniqueId",
  });

  useEffect(() => {
    if (programTemplateDataById && isNotEmpty(programTemplateDataById)) {
      const index = programTemplateSelectedIndexRef.current;

      setValue(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.programClassName}`,
        programTemplateDataById.programClass,
      );
      setValue(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.solutionOfferingsName}`,
        programTemplateDataById.solutionOfferings,
      );
      setValue(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.programTemplateName}`,
        programTemplateDataById.name,
      );

      const countriesData = isNotEmpty(programTemplateDataById.countries)
        ? programTemplateDataById.countries
        : allCountriesList;
      const countryIds = getCountryIdsAsStringArray(countriesData);
      setValue(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.countries}`,
        countryIds,
      );

      setValue(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.allCountries}`,
        countriesData,
      );

      if (index !== undefined) {
        setValue(
          `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.programTemplateDropdownChangeStatus}`,
          isEdit,
        );

        const updatedCountriesList = [...countriesList];
        updatedCountriesList[index] = isNotEmpty(
          programTemplateDataById.countries,
        )
          ? setApiDataToDropDownOptionForCountry(
              programTemplateDataById.countries,
            )
          : allCountriesList;
        setCountriesList(updatedCountriesList);

        const updatedPreSelectedCountries = [...preSelectedCountries];
        updatedPreSelectedCountries[index] = countryIds;
        setPreSelectedCountries(updatedPreSelectedCountries);
      }
    }
  }, [programTemplateDataById]);

  useEffect(() => {
    const updatedPreSelectedCountries = [...preSelectedCountries];
    const updatedCountriesList = [...countriesList];

    programTemplatesArray.forEach((_field, index) => {
      const countryIds = getUpdatedCountryIds(index);
      if (isNotEmpty(countryIds)) {
        updatedPreSelectedCountries[index] = countryIds;
      }

      let countriesForIndexedProgramTemplate = getCountriesForTemplate(index);

      if (
        countriesForIndexedProgramTemplate &&
        isNotEmpty(countriesForIndexedProgramTemplate)
      ) {
        if (
          isObject(countriesForIndexedProgramTemplate[0]) &&
          hasKey(countriesForIndexedProgramTemplate[0], "label") &&
          hasKey(countriesForIndexedProgramTemplate[0], "value")
        ) {
          updatedCountriesList[index] = countriesForIndexedProgramTemplate;
        } else if (hasCountryNameKey(countriesForIndexedProgramTemplate)) {
          updatedCountriesList[index] = setApiDataToDropDownOptionForCountry(
            countriesForIndexedProgramTemplate,
          );
        }
      }
    });
    setCountriesList(updatedCountriesList);
    setPreSelectedCountries(updatedPreSelectedCountries);
  }, []);

  const getUpdatedCountryIds = (index: number) => {
    let countryIds = getValues(
      `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.countries}`,
    );
    if (isEdit && isNotEmpty(countryIds) && hasCountryNameKey(countryIds)) {
      countryIds = getCountryIdsAsStringArray(countryIds);
    }
    return countryIds;
  };

  const getCountriesForTemplate = (index: number) => {
    const resourceGroupProgramTemplateMapId = isEdit
      ? getValues(
          `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.resourceGroupProgramTemplateMapId}`,
        )
      : "";
    const programTemplateDropdownChangeStatus = getValues(
      `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.programTemplateDropdownChangeStatus}`,
    );
    return isNotEmpty(resourceGroupProgramTemplateMapId) &&
      !programTemplateDropdownChangeStatus
      ? getValues(
          `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.countries}`,
        )
      : getValues(
          `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.allCountries}`,
        );
  };

  const removeCountriesFromState = (index?: number) => {
    const updatedPreSelectedCountries = [...preSelectedCountries];
    updatedPreSelectedCountries.splice(index!!, 1);
    setPreSelectedCountries(updatedPreSelectedCountries);

    const updatedCountriesList = [...countriesList];
    updatedCountriesList.splice(index!!, 1);
    setCountriesList(updatedCountriesList);
  };

  const getLabel = (key: string) => {
    return fetchLabel(clientSpecificContentTitle, key);
  };

  const handleAddProgramTemplate = () => {
    append(emptyProgramTemplateItem);
  };

  const handleResourceAccordionStatus = (index: number, expanded: boolean) => {
    const status = [...programTemplateAccordionStatus];
    status[index] = expanded;
    setProgramTemplateAccordionStatus(status);
  };

  useEffect(() => {
    let status = [...programTemplateAccordionStatus];
    programTemplatesArray.forEach((_field, index) => {
      status[index] = isEdit
        ? isEdit
        : index === programTemplatesArray.length - 1;
    });
    setProgramTemplateAccordionStatus(status);
  }, [programTemplatesArray.length]);

  const handleModalClose = () => setShowDeleteConfirmationModal(false);

  const handleDeleteProgramTemplate = () => {
    if (isNotEmpty(indexRef.current)) {
      remove(indexRef.current);
      removeCountriesFromState(indexRef.current);
      handleModalClose();
      displayToast(
        fetchLabel(
          billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
          billingAndPurchaseOrderKeys.programTemplateDeletedMessage,
        ),
        "success",
      );
      indexRef.current = undefined;
    }
  };

  const clearCountriesError = (index: number) => {
    if (isNotEmpty(errors)) {
      clearErrors(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.countries}`,
      );
    }
  };

  const handleItemChangedOrDropdownClosed = (
    selectedValues: Array<string | number>,
    index: number,
  ) => {
    if (isEmpty(selectedValues)) {
      setError(
        `${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.countries}`,
        {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        },
      );
    } else {
      clearCountriesError(index);
    }
  };

  const onDeleteProgramTemplateClick = (index: number) => {
    indexRef.current = index;
    setShowDeleteConfirmationModal(true);
  };

  const handleProgramTemplateIdChange = (id: string, index: number) => {
    if (isEmpty(id)) {
      return;
    }
    clearCountriesError(index);
    programTemplateSelectedIndexRef.current = index;
    getProgramTemplateById(id);
  };

  const programTemplateFromArrayHasErrors = (index: number) =>
    getFormArrayInstanceHasErrors(
      errors,
      index,
      ProgramTemplateFormKey.programTemplates,
      [
        ProgramTemplateFormKey.programTemplateId,
        ProgramTemplateFormKey.countries,
      ],
    );

  const renderSubHeader = (index: number) => {
    return programTemplatesArray.length > 1 ? (
      <>
        <MuiBox className="semi-header">
          <ButtonWithIcon
            data-testId={`${pageId}-${ProgramTemplateFormKey.delete}`}
            buttonText={commonLabels.delete}
            onActionClick={() => onDeleteProgramTemplateClick(index)}
            ActionIcon={DeleteIcon}
          />
        </MuiBox>
        <Separator />
      </>
    ) : (
      <></>
    );
  };

  return (
    <MuiBox data-testid="program-templates-tab">
      <InformationBadge
        className="info-batch"
        message={getLabel(
          isEdit ? editProgramTemplateNote : addProgramTemplateNote,
        )}
      />
      {programTemplatesArray.map((field, index) => {
        const label = replaceHashWithValue(
          getLabel(programTemplateHeading),
          `${index + 1}`,
        );
        return (
          <AccordionHeader
            key={field.ptUniqueId}
            title={
              <AccordionHeaderText
                label={label}
                data-testId={`${pageId}-${ProgramTemplateFormKey.header}`}
              />
            }
            className="accordion-resource-header"
            onChange={(e, expanded) =>
              handleResourceAccordionStatus(index, expanded)
            }
            isExpanded={programTemplateAccordionStatus[index]}
            isFormArrayError={!programTemplateFromArrayHasErrors(index)}
          >
            {renderSubHeader(index)}
            <MuiGrid container columnSpacing={2} className="flex-row">
              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.programTemplateId}`}
                >
                  <MuiSelect
                    label={getLabel(programTemplate)}
                    options={programTemplatesData}
                    visibleDefaultSelectOption
                    id={`${pageId}-${ProgramTemplateFormKey.programTemplateId}-${index}`}
                    isMandatory
                    showSearch
                    placeholder={selectPlaceHolder}
                    onChange={(event) =>
                      handleProgramTemplateIdChange(
                        event.target.value as string,
                        index,
                      )
                    }
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.solutionOfferingsName}`}
                >
                  <MuiTextField
                    label={getLabel(solutionOfferings)}
                    id={`${pageId}-${ProgramTemplateFormKey.solutionOfferingsName}-${index}`}
                    disabled
                    placeholder={inputPlaceHolder}
                    maxLength={MAX_LENGTH_100}
                    showCloseIcon
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.programClassName}`}
                >
                  <MuiTextField
                    label={getLabel(programClass)}
                    id={`${pageId}-${ProgramTemplateFormKey.programClassName}-${index}`}
                    disabled
                    placeholder={inputPlaceHolder}
                    showCloseIcon
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ProgramTemplateFormKey.programTemplates}[${index}].${ProgramTemplateFormKey.countries}`}
                >
                  <MuiMultiSelect
                    label={getLabel(country)}
                    options={countriesList[index] ?? []}
                    id={`${pageId}-${ProgramTemplateFormKey.countries}-${index}`}
                    isMandatory
                    showSelectAllOption
                    showSearch
                    placeholder={selectPlaceHolder}
                    onItemsChangedOrDropdownClosed={(
                      selectedValues: Array<string | number>,
                    ) =>
                      handleItemChangedOrDropdownClosed(selectedValues, index)
                    }
                    showChip
                    dialogTitle={selectedCountries}
                    selectedValues={preSelectedCountries[index]}
                    selectPlaceHolder={commonLabels.selectPlaceHolder}
                    searchPlaceHolder={commonLabels.search}
                    selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                    doneText={commonLabels.done}
                    allARIAProps={multiSelectARIALabels}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
          </AccordionHeader>
        );
      })}
      <MuiBox className="add-resource-button-container">
        <MuiButtonTertiary
          onClick={handleAddProgramTemplate}
          id={`${pageId}-${ProgramTemplateFormKey.addTemplateButton}`}
          className="add-template-button"
        >
          <AddIcon className="add-icon" />
          {getLabel(addTemplateButtonText)}
        </MuiButtonTertiary>
      </MuiBox>
      {showDeleteConfirmationModal && (
        <WarningConfirmationModal
          show={showDeleteConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteProgramTemplate}
          labelContent={fetchLabel(
            billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
            billingAndPurchaseOrderKeys.deleteProgramTemplateActionText,
          )}
        />
      )}
    </MuiBox>
  );
};
